import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTimeout } from 'ahooks';
import {
  autoBetState,
  autoCrashOutAtState,
  bettedAmountState,
  crashedTimeState,
  multiplierState,
  nextRoundBetValueState,
  noBetState,
  noNextRoundBetState,
  stopAtState,
} from '../state';
import { BetParams, GameStatus } from '../types/crash';
import { crashBet, crashCashout, currentBet } from '@/apis/casino/crash';
import { Multiply } from '@/utils/decimal/operations';
import { useMutation } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { useCelebration } from '@/components/features/casino/game-celebration';
import { queryClient } from '@/reactQueryKeys/queryClient';
import useTheme from '@/theme/useTheme';
import {
  currentCurrencyState,
  isTemporaryLoginWithoutToken,
} from '@/state/userInfo';
import { formatMoneyByCurrency } from '@/utils';
import { getCrashDecimal } from '@/constants/decimal';
import { floorToNString } from '@/components/features/casino/crash/GameBox/utils';
import {
  useCrashBetSound,
  useCrashBgSound,
  useCrashWinSound,
} from '@/components/features/casino/crash/BgSoundManage/useCrashMusic';
import useBindEmailOrLogin from '@/hooks/useBindEmailOrLogin';

interface CommonProps {
  sx: Record<string, string | number | undefined>;
}

export const useBetStyle = (isPc) => {
  const { theme } = useTheme();
  const sx = useMemo(() => {
    if (isPc) {
      return {
        height: '114px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      };
    } else {
      return {
        height: '53px',
        width: '100%',
        marginTop: '8px',
        display: 'flex',
        flexDirection: 'column',
      };
    }
  }, [isPc, theme]);
  const commonProps: CommonProps = useMemo(() => {
    return {
      sx,
    };
  }, [sx]);
  return commonProps;
};

export const useReloadBetState = (userLoggedIn) => {
  const [flag, setFlag] = useState(false);
  // need to wait for resetState(GameBox:530) to finish
  useTimeout(() => {
    setFlag(true);
  }, 2000);
  const setBettedAmount = useSetAtom(bettedAmountState);
  const setStopAtValue = useSetAtom(stopAtState);
  const setCrashedTime = useSetAtom(crashedTimeState);
  const { data: curBet } = useQuery({
    enabled: userLoggedIn && flag,
    queryKey: ['curCrashBet'],
    queryFn: async () => {
      const res = await currentBet();
      return res.data ? res.data : 0;
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (curBet) {
      console.log('curBet', curBet);
      const { amount, payoutMultiplier, elapsed } = curBet;
      setBettedAmount(amount);
      setStopAtValue(payoutMultiplier);
      if (elapsed && elapsed > 0) {
        setCrashedTime(Number(elapsed) / 1000);
      }
    }
  }, [curBet]);
};

export const useBet = ({
  userLoggedIn,
  betAmount,
  stopAt,
  gameStatus,
  openLoginModal,
  currentCurrency,
}) => {
  const { playCrashBg, pauseCrashBg } = useCrashBgSound();
  const { playCrashBetSound } = useCrashBetSound();
  const { requirementToBindEmailOrLogin } = useBindEmailOrLogin();

  const [nextRoundBetValue, setNextRoundBetValue] = useAtom(
    nextRoundBetValueState,
  );
  const noNextRoundBet = useAtomValue(noNextRoundBetState);
  const hasNextRoundBet = !noNextRoundBet;

  const [bettedAmount, setBettedAmount] = useAtom(bettedAmountState);
  const noBet = useAtomValue(noBetState);
  const hasBet = !noBet;

  const [stopAtValue, setStopAtValue] = useAtom(stopAtState);
  const setCrashedTime = useSetAtom(crashedTimeState);
  const [autoBet] = useAtom(autoBetState);

  const { mutate: betMutate, isLoading: betLoading } = useMutation(
    (data: BetParams) => {
      if (!data.payoutMultiplier) {
        delete data.payoutMultiplier;
      }
      return crashBet(data);
    },
    {
      onSuccess: (res) => {
        if (res.status.code === '0') {
          setBettedAmount(res.data.amount);
          if (res.data.payoutMultiplier && res.data.payoutMultiplier > 1) {
            setStopAtValue(res.data.payoutMultiplier);
          }
        }
      },
    },
  );

  const handleBet = () => {
    if (requirementToBindEmailOrLogin()) return;
    playCrashBg();
    playCrashBetSound();
    // emitPlayCrashBgSound();
    // emitPlayCrashBetSound();
    betMutate({
      amount: betAmount,
      currency: currentCurrency,
      payoutMultiplier: stopAt,
    });
    //调用接口
  };

  const handleBetNextRound = () => {
    if (requirementToBindEmailOrLogin()) return;
    playCrashBg();
    playCrashBetSound();
    // emitPlayCrashBgSound();
    // emitPlayCrashBetSound();
    setNextRoundBetValue(betAmount);
  };

  const handleCancel = () => {
    setNextRoundBetValue(-1);
  };
  //🐔next round bet
  useEffect(() => {
    if (!userLoggedIn) return;
    if (gameStatus === GameStatus.Starting && hasNextRoundBet) {
      betMutate(
        {
          amount: nextRoundBetValue,
          currency: currentCurrency,
          payoutMultiplier: stopAt,
        },
        {
          onSuccess: (res: any) => {
            setBettedAmount(res.data.amount);
            setStopAtValue(res.data.payoutMultiplier);
            setNextRoundBetValue(-1);
          },
          onError: () => {
            setNextRoundBetValue(-1);
          },
        },
      );
    }
  }, [gameStatus, nextRoundBetValue, hasNextRoundBet, stopAt, userLoggedIn]);

  //🐔auto bet
  useEffect(() => {
    if (!userLoggedIn) return;
    if (betLoading) return;
    if (gameStatus === GameStatus.Starting && autoBet && noBet) {
      // nextRoundBet与autoBet会冲突，导致调用两次bet接口
      if (hasNextRoundBet) return;
      betMutate({
        amount: betAmount,
        currency: currentCurrency,
        payoutMultiplier: stopAt,
      });
    }
  }, [
    gameStatus,
    autoBet,
    noBet,
    stopAt,
    hasNextRoundBet,
    betLoading,
    userLoggedIn,
  ]);

  //🐔清空上一局的bet value
  //注意:在Staring阶段刷新页面时，会出现把当前局的betValue也清空的情况,下面的代码是为了处理这个问题
  useEffect(() => {
    if (gameStatus === GameStatus.Starting) {
      setBettedAmount(-1);
    }
  }, [gameStatus, setBettedAmount]);
  // 🐔fix:处理因页面刷新导致的bet value丢失
  useReloadBetState(userLoggedIn);

  return {
    hasNextRoundBet,
    nextRoundBetValue,
    noBet,
    hasBet,
    bettedAmount,
    betLoading,
    handleBet,
    handleBetNextRound,
    handleCancel,
  };
};

export const useCrashOut = ({ noBet, bettedAmount, gameStatus }) => {
  const { playCrashWinSound } = useCrashWinSound();
  const { celebrate } = useCelebration();
  const [crashedTime, setCrashedTime] = useAtom(crashedTimeState);
  const multiplier = useAtomValue(multiplierState);
  const [stopAtValue, setStopAtValue] = useAtom(stopAtState);
  const currentCurrency = useAtomValue(currentCurrencyState);
  const autoCrashOut = useAtomValue(autoCrashOutAtState);

  const profit = useMemo(() => {
    if (bettedAmount === 0) return 0;
    const decimal = getCrashDecimal(currentCurrency);
    return floorToNString(
      Number(Multiply(multiplier, bettedAmount, 8)),
      decimal,
    );
  }, [bettedAmount, multiplier, currentCurrency]);

  const resetStopAtState = () => {
    setStopAtValue(null);
  };
  const { mutate: crashOutMutate, isLoading: crashLoading } = useMutation(
    (multiplier: number) => {
      return crashCashout(multiplier);
    },
    {
      onSuccess: (res: any) => {
        if (res.data) {
          const { elapsed, payout, currency, cashOutAt } = res.data;
          setCrashedTime(Number(elapsed) / 1000);
          // emitPlayCrashWinSound();
          playCrashWinSound();

          resetStopAtState();
          const profit = formatMoneyByCurrency(payout, { currency });
          Number(profit) > 0 &&
            celebrate({
              profit: profit + ' ' + currency,
              multiplier: Number(cashOutAt),
            });
        }
      },
      retry: 0,
      onError: () => {
        resetStopAtState();
      },
    },
  );
  const handleCrashOut = () => {
    crashOutMutate(multiplier);
  };

  //🐔自动crashOut(保证只调用一次)
  // @BUG::登录的时候会自动调用该接口
  useEffect(() => {
    if (gameStatus !== GameStatus.InProgress) return;
    if (noBet) return;
    if (crashedTime) return;
    if (crashLoading) return;
    if (!autoCrashOut) return;
    if (!stopAtValue) return;
    if (Number(stopAtValue) === 0) return;
    if (Number(multiplier) >= Number(stopAtValue)) {
      crashOutMutate(multiplier);
      resetStopAtState();
    }
  }, [
    stopAtValue,
    multiplier,
    noBet,
    handleCrashOut,
    crashedTime,
    crashLoading,
    gameStatus,
  ]);

  return {
    autoCrashOut,
    crashedTime,
    profit,
    multiplier,
    crashLoading,
    handleCrashOut,
  };
};
